import { useState, useCallback, useRef } from 'react';

interface ContactFormData {
  [key: string]: string;
}

interface UseContactFormOptions {
  endpoint?: string;
  onSuccess?: () => void;
  onError?: (error: string) => void;
  debounceMs?: number;
}

export function useContactForm({
  endpoint = '/api/contact',
  onSuccess,
  onError,
  debounceMs = 1000,
}: UseContactFormOptions = {}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lastSubmitTime = useRef<number>(0);

  const submitForm = useCallback(
    async (formData: ContactFormData) => {
      const now = Date.now();
      if (now - lastSubmitTime.current < debounceMs) {
        return; // Debounce
      }
      lastSubmitTime.current = now;

      // Anti-spam honeypot check (assume the form has a field named 'honeypot')
      if (formData.honeypot) {
        // Silently succeed for bots
        setIsSuccess(true);
        onSuccess?.();
        return;
      }

      setIsSubmitting(true);
      setError(null);
      setIsSuccess(false);

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (!response.ok) {
          throw new Error('Failed to submit form');
        }

        setIsSuccess(true);
        onSuccess?.();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'An unexpected error occurred';
        setError(errorMessage);
        onError?.(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [endpoint, onSuccess, onError, debounceMs]
  );

  return {
    isSubmitting,
    isSuccess,
    error,
    submitForm,
  };
}
